import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Input, Space, Row, Col, Statistic,
    Typography, Tabs, Avatar, Button, DatePicker, Dropdown, message,
    Modal, Segmented, Divider
} from 'antd';
import {
    SearchOutlined, ThunderboltOutlined,
    TeamOutlined, CrownOutlined, ReloadOutlined,
    EyeOutlined, DollarOutlined, CalendarOutlined,
    DownloadOutlined, FileExcelOutlined, CheckCircleOutlined,
    ClockCircleOutlined, WalletOutlined, EditOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import locale from 'antd/es/date-picker/locale/fr_FR';
import XLSX from 'xlsx-js-style';

dayjs.locale('fr');

const { RangePicker } = DatePicker;

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const RevenuesBonusRechargePage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [bonuses, setBonuses] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [exporting, setExporting] = useState(false);

    // Payment modal states
    const [paymentModal, setPaymentModal] = useState({ visible: false, bonus: null });
    const [paymentMode, setPaymentMode] = useState('wave');
    const [paymentRef, setPaymentRef] = useState('');
    const [sendingWavePayout, setSendingWavePayout] = useState(false);
    const [savingPayment, setSavingPayment] = useState(false);

    const [stats, setStats] = useState({
        totalBonuses: 0,
        totalAmount: 0,
        affiliateCount: 0,
        affiliateAmount: 0,
        ambassadorCount: 0,
        ambassadorAmount: 0,
        pendingAmount: 0,
        paidAmount: 0
    });

    const fetchBonuses = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (activeTab !== 'all') {
                params.recipientType = activeTab;
            }

            // Ajouter les filtres de période
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.startDate = dateRange[0].startOf('day').toISOString();
                params.endDate = dateRange[1].endOf('day').toISOString();
            }

            const response = await affiliatesService.getSameDayRechargeBonuses(params);
            if (response.success) {
                setBonuses(response.data || []);
                if (response.stats) {
                    setStats(response.stats);
                }
            }
        } catch (error) {
            console.error('Erreur chargement bonus:', error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, dateRange]);

    useEffect(() => {
        fetchBonuses();
    }, [fetchBonuses]);

    const filteredBonuses = bonuses.filter(b => {
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        const name = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
        const phone = (b.phone || '').toLowerCase();
        return name.includes(search) || phone.includes(search);
    });

    const handleViewDetails = (record) => {
        navigate(`/dashboard/revenues-bonus-recharge/${record.recipientType}/${record.beneficiaryId}`);
    };

    // Open payment modal
    const handlePaymentClick = (record) => {
        setPaymentModal({ visible: true, bonus: record });
        setPaymentMode('wave');
        setPaymentRef('');
    };

    // Wave payout handler (per commission)
    const handleWavePayout = async () => {
        const bonus = paymentModal.bonus;
        if (!bonus?.commissionId) {
            message.error('Impossible de payer: commission non trouvée');
            return;
        }

        const amountToPay = bonus.amount || 0;
        if (amountToPay <= 0) {
            message.warning('Aucun montant à payer');
            return;
        }

        Modal.confirm({
            title: 'Confirmer le paiement Wave',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Vous allez envoyer <strong>{amountToPay.toLocaleString()} F</strong> via Wave à :</p>
                    <p><strong>{bonus.firstName} {bonus.lastName}</strong></p>
                    <p style={{ color: '#8c8c8c' }}>{bonus.phone}</p>
                    <p style={{ marginTop: 12, color: '#faad14' }}>
                        Cette action est irréversible. L'argent sera débité de votre compte Wave Business.
                    </p>
                </div>
            ),
            okText: 'Confirmer et payer',
            cancelText: 'Annuler',
            okButtonProps: { style: { background: '#1890ff' } },
            onOk: async () => {
                setSendingWavePayout(true);
                try {
                    const response = await affiliatesService.sendBonusRechargeWavePayout({
                        commissionId: bonus.commissionId,
                        amount: amountToPay
                    });

                    if (response.success) {
                        message.success(`Paiement Wave effectué avec succès ! Référence: ${response.data.transactionId}`);
                        setPaymentModal({ visible: false, bonus: null });
                        setPaymentRef('');
                        fetchBonuses();
                    } else {
                        message.error(response.message || 'Erreur lors du paiement Wave');
                    }
                } catch (error) {
                    console.error('Erreur Wave Payout:', error);
                    message.error(error.response?.data?.message || 'Erreur lors du paiement Wave');
                } finally {
                    setSendingWavePayout(false);
                }
            }
        });
    };

    // Manual payment handler (per commission)
    const handleSavePayment = async () => {
        const bonus = paymentModal.bonus;
        if (!bonus?.commissionId) {
            message.error('Impossible de sauvegarder: commission non trouvée');
            return;
        }

        setSavingPayment(true);
        try {
            const response = await affiliatesService.paySameDayRechargeBonus(bonus.commissionId, {
                paymentStatus: 'paid',
                paymentReference: paymentRef,
                paymentMethod: 'manual'
            });

            if (response.success) {
                message.success('Paiement enregistré avec succès');
                setPaymentModal({ visible: false, bonus: null });
                setPaymentRef('');
                fetchBonuses();
            } else {
                message.error(response.message || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Erreur sauvegarde paiement:', error);
            message.error('Erreur lors de la sauvegarde du paiement');
        } finally {
            setSavingPayment(false);
        }
    };

    const getTypeTag = (type) => {
        if (type === 'affiliate') {
            return <Tag color="blue" icon={<TeamOutlined />}>Affilie</Tag>;
        }
        return <Tag color="purple" icon={<CrownOutlined />}>Ambassadeur</Tag>;
    };

    const getStatusTag = (status) => {
        if (status === 'paid') {
            return <Tag color="cyan" icon={<CheckCircleOutlined />}>Payé</Tag>;
        }
        return <Tag color="orange" icon={<ClockCircleOutlined />}>En attente de paiement</Tag>;
    };

    // Fonction pour préparer les données d'export
    const prepareExportData = () => {
        const dataToExport = filteredBonuses.filter(b => activeTab === 'all' || b.recipientType === activeTab);
        return dataToExport.map(b => ({
            'Nom': `${b.firstName || ''} ${b.lastName || ''}`.trim(),
            'Téléphone': b.phone || '',
            'Type': b.recipientType === 'affiliate' ? 'Affilié' : 'Ambassadeur',
            'Date': b.date ? dayjs(b.date).format('DD/MM/YYYY') : '',
            'Montant (F)': b.amount || 0,
            'Statut': b.status === 'paid' ? 'Payé' : 'En attente',
            'Référence': b.paymentReference || ''
        }));
    };

    // Export CSV
    const exportToCSV = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            let titleLine = 'BONUS RECHARGE JOUR - TOUS LES BÉNÉFICIAIRES';
            if (activeTab === 'affiliate') titleLine = 'BONUS RECHARGE JOUR - AFFILIÉS';
            else if (activeTab === 'ambassador') titleLine = 'BONUS RECHARGE JOUR - AMBASSADEURS';

            let periodLine = '';
            if (dateRange && dateRange[0] && dateRange[1]) {
                periodLine = `PÉRIODE: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`;
            } else {
                periodLine = `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;
            }

            const headers = Object.keys(data[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '',
                headers.join(';'),
                ...data.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const filename = `bonus_recharge_jour_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    // Export Excel (XLS)
    const exportToExcel = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            // Styles
            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "FAAD14" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            let titleText = 'BONUS RECHARGE JOUR - TOUS LES BÉNÉFICIAIRES';
            if (activeTab === 'affiliate') titleText = 'BONUS RECHARGE JOUR - AFFILIÉS';
            else if (activeTab === 'ambassador') titleText = 'BONUS RECHARGE JOUR - AMBASSADEURS';

            let periodText = '';
            if (dateRange && dateRange[0] && dateRange[1]) {
                periodText = `PÉRIODE: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`;
            } else {
                periodText = `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;
            }

            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                []
            ];

            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            const headers = Object.keys(data[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...data.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Bonus Recharge');

            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length), 15) + 2
            }));
            worksheet['!cols'] = colWidths;

            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }
            ];

            worksheet['!rows'] = [
                { hpt: 25 },
                { hpt: 20 },
                { hpt: 15 },
                { hpt: 22 }
            ];

            const filename = `bonus_recharge_jour_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    // Menu d'export
    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    const columns = [
        {
            title: 'Beneficiaire',
            key: 'beneficiary',
            width: 260,
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar
                        size={45}
                        icon={record.recipientType === 'affiliate' ? <TeamOutlined /> : <CrownOutlined />}
                        style={{
                            backgroundColor: record.recipientType === 'affiliate' ? '#1890ff' : '#722ed1',
                            flexShrink: 0
                        }}
                    />
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {record.firstName} {record.lastName}
                        </div>
                        <div style={{ color: '#888', fontSize: 12 }}>{record.phone}</div>
                        {getTypeTag(record.recipientType)}
                    </div>
                </div>
            )
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 130,
            sorter: (a, b) => new Date(a.date) - new Date(b.date),
            render: (date) => (
                <div>
                    <CalendarOutlined style={{ marginRight: 6, color: '#888' }} />
                    {dayjs(date).format('DD/MM/YYYY')}
                </div>
            )
        },
        {
            title: 'Montant',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
            render: (amount) => (
                <div style={{
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: '#3f8600'
                }}>
                    {(amount || 0).toLocaleString()} F
                </div>
            )
        },
        {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            width: 180,
            filters: [
                { text: 'En attente de paiement', value: 'pending' },
                { text: 'Payé', value: 'paid' }
            ],
            onFilter: (value, record) => {
                if (value === 'pending') return record.status !== 'paid';
                return record.status === 'paid';
            },
            render: (status, record) => (
                <div>
                    {getStatusTag(status)}
                    {status === 'paid' && record.paymentReference && (
                        <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                            Ref: {record.paymentReference}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    {record.status !== 'paid' && (
                        <Button
                            type="primary"
                            icon={<DollarOutlined />}
                            onClick={() => handlePaymentClick(record)}
                            style={{ background: '#52c41a', borderColor: '#52c41a' }}
                        >
                            Payer
                        </Button>
                    )}
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(record)}
                    >
                        Detail
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div className="bonus-recharge-page">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <ThunderboltOutlined style={{ marginRight: 12, color: '#faad14' }} />
                        Bonus Recharge Jour
                    </Title>
                    <Text type="secondary">
                        Liste des bonus recharge (2000 F quand 2+ nouveaux chauffeurs rechargent le jour de leur inscription)
                    </Text>
                </div>
                <Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} loading={exporting}>
                            Exporter
                        </Button>
                    </Dropdown>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchBonuses}
                        loading={loading}
                    >
                        Actualiser
                    </Button>
                </Space>
            </div>

            {/* Statistiques */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><ThunderboltOutlined style={{ marginRight: 8, color: '#faad14' }} />Total bonus</span>}
                            value={stats.totalBonuses}
                            valueStyle={{ color: '#faad14' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Montant: <strong style={{ color: '#3f8600' }}>{(stats.totalAmount || 0).toLocaleString()} F</strong>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />Affilies</span>}
                            value={stats.affiliateCount}
                            valueStyle={{ color: '#1890ff' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Montant: <strong>{(stats.affiliateAmount || 0).toLocaleString()} F</strong>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><CrownOutlined style={{ marginRight: 8, color: '#722ed1' }} />Ambassadeurs</span>}
                            value={stats.ambassadorCount}
                            valueStyle={{ color: '#722ed1' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Montant: <strong>{(stats.ambassadorAmount || 0).toLocaleString()} F</strong>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><DollarOutlined style={{ marginRight: 8, color: '#52c41a' }} />Paye</span>}
                            value={(stats.paidAmount || 0).toLocaleString()}
                            suffix="F"
                            valueStyle={{ color: '#52c41a' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            En attente: <strong style={{ color: '#faad14' }}>{(stats.pendingAmount || 0).toLocaleString()} F</strong>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Recherche et filtres */}
            <Card style={{ marginBottom: 16 }}>
                <Space wrap size="middle">
                    <Input
                        placeholder="Rechercher par nom ou téléphone..."
                        prefix={<SearchOutlined />}
                        style={{ width: 280 }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                    <RangePicker
                        placeholder={['Date début', 'Date fin']}
                        format="DD/MM/YYYY"
                        value={dateRange}
                        onChange={(dates) => setDateRange(dates)}
                        allowClear
                        style={{ width: 280 }}
                        locale={locale}
                        presets={[
                            { label: 'Aujourd\'hui', value: [dayjs(), dayjs()] },
                            { label: 'Cette semaine', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
                            { label: 'Ce mois', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                            { label: 'Mois dernier', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                            { label: '3 derniers mois', value: [dayjs().subtract(3, 'month').startOf('month'), dayjs()] }
                        ]}
                    />
                    {dateRange && (
                        <Tag color="gold" closable onClose={() => setDateRange(null)}>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {dateRange[0]?.format('DD/MM/YYYY')} - {dateRange[1]?.format('DD/MM/YYYY')}
                        </Tag>
                    )}
                </Space>
            </Card>

            {/* Onglets et tableau */}
            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane
                        tab={<span><ThunderboltOutlined /> Tous ({bonuses.length})</span>}
                        key="all"
                    />
                    <TabPane
                        tab={<span><TeamOutlined style={{ color: '#1890ff' }} /> Affilies ({bonuses.filter(b => b.recipientType === 'affiliate').length})</span>}
                        key="affiliate"
                    />
                    <TabPane
                        tab={<span><CrownOutlined style={{ color: '#722ed1' }} /> Ambassadeurs ({bonuses.filter(b => b.recipientType === 'ambassador').length})</span>}
                        key="ambassador"
                    />
                </Tabs>

                <Table
                    columns={columns}
                    dataSource={filteredBonuses}
                    rowKey={(record) => record.commissionId}
                    loading={loading}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => `${total} bonus`,
                        defaultPageSize: 20
                    }}
                    scroll={{ x: 900 }}
                    size="middle"
                />
            </Card>

            {/* Modal de paiement */}
            <Modal
                title={
                    <span>
                        <DollarOutlined style={{ marginRight: 8 }} />
                        Enregistrer un paiement
                    </span>
                }
                open={paymentModal.visible}
                onCancel={() => {
                    setPaymentModal({ visible: false, bonus: null });
                    setPaymentRef('');
                }}
                footer={paymentMode === 'manual' ? [
                    <Button key="cancel" onClick={() => {
                        setPaymentModal({ visible: false, bonus: null });
                        setPaymentRef('');
                    }}>
                        Annuler
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={savingPayment}
                        onClick={handleSavePayment}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Enregistrer le paiement
                    </Button>
                ] : null}
                width={520}
            >
                {paymentModal.bonus && (() => {
                    const b = paymentModal.bonus;
                    const amountToPay = b.amount || 0;
                    const isAmbassador = b.recipientType === 'ambassador';

                    return (
                        <>
                            {/* Infos du bonus */}
                            <div style={{
                                background: '#fafafa',
                                borderRadius: 8,
                                padding: 16,
                                marginBottom: 16
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <Avatar
                                        size={48}
                                        icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                                        style={{
                                            backgroundColor: isAmbassador ? '#722ed1' : '#1890ff'
                                        }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 16 }}>
                                            {b.firstName} {b.lastName}
                                        </div>
                                        <div style={{ color: '#888' }}>{b.phone}</div>
                                        <Tag color={isAmbassador ? 'purple' : 'blue'} style={{ marginTop: 4 }}>
                                            {isAmbassador ? 'Ambassadeur' : 'Affilié'}
                                        </Tag>
                                    </div>
                                </div>
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <div style={{ fontSize: 12, color: '#888' }}>Date</div>
                                        <div style={{ fontWeight: 600 }}>
                                            {dayjs(b.date).format('DD/MM/YYYY')}
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ fontSize: 12, color: '#888' }}>Montant</div>
                                        <div style={{ fontWeight: 600, color: '#faad14' }}>
                                            {amountToPay.toLocaleString()} F
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ fontSize: 12, color: '#888' }}>Statut</div>
                                        {getStatusTag(b.status)}
                                    </Col>
                                </Row>
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            {/* Sélecteur de mode de paiement */}
                            <div style={{ marginBottom: 20 }}>
                                <Segmented
                                    value={paymentMode}
                                    onChange={(value) => {
                                        setPaymentMode(value);
                                        setPaymentRef('');
                                    }}
                                    block
                                    size="large"
                                    options={[
                                        {
                                            value: 'wave',
                                            label: (
                                                <div style={{ padding: '4px 0' }}>
                                                    <WalletOutlined style={{ marginRight: 6 }} />
                                                    Payer via Wave
                                                </div>
                                            )
                                        },
                                        {
                                            value: 'manual',
                                            label: (
                                                <div style={{ padding: '4px 0' }}>
                                                    <EditOutlined style={{ marginRight: 6 }} />
                                                    Saisie manuelle
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            </div>

                            {/* MODE WAVE */}
                            {paymentMode === 'wave' && (
                                <div style={{
                                    background: '#f0f5ff',
                                    border: '1px solid #adc6ff',
                                    borderRadius: 10,
                                    padding: 20
                                }}>
                                    <div style={{ marginBottom: 16, textAlign: 'center' }}>
                                        <div style={{ fontSize: 13, color: '#595959' }}>
                                            Le paiement de <strong>{amountToPay.toLocaleString()} F</strong> sera envoyé à :
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: 16, marginTop: 4 }}>
                                            {b.firstName} {b.lastName}
                                        </div>
                                        <div style={{ color: '#8c8c8c' }}>{b.phone}</div>
                                    </div>

                                    <Button
                                        type="primary"
                                        size="large"
                                        block
                                        icon={<WalletOutlined />}
                                        loading={sendingWavePayout}
                                        onClick={handleWavePayout}
                                        style={{
                                            background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)',
                                            border: 'none',
                                            height: 48,
                                            fontWeight: 600,
                                            borderRadius: 8
                                        }}
                                    >
                                        Envoyer {amountToPay.toLocaleString()} F via Wave
                                    </Button>
                                    <div style={{ marginTop: 8, fontSize: 11, color: '#5b8c00', textAlign: 'center' }}>
                                        Le paiement sera envoyé directement sur Wave et la référence sera automatiquement enregistrée
                                    </div>
                                </div>
                            )}

                            {/* MODE MANUEL */}
                            {paymentMode === 'manual' && (
                                <div style={{
                                    background: '#fffbe6',
                                    border: '1px solid #ffe58f',
                                    borderRadius: 10,
                                    padding: 20
                                }}>
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ marginBottom: 6, fontWeight: 500 }}>Référence de paiement</div>
                                        <Input
                                            placeholder="Ex: TX-2025021001234"
                                            value={paymentRef}
                                            onChange={(e) => setPaymentRef(e.target.value)}
                                            size="large"
                                            prefix={<WalletOutlined style={{ color: '#bfbfbf' }} />}
                                        />
                                        <div style={{ marginTop: 6, fontSize: 11, color: '#8c8c8c' }}>
                                            Entrez la référence si vous avez déjà effectué le paiement
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center', fontSize: 13, color: '#595959' }}>
                                        Montant : <strong style={{ color: '#faad14' }}>{amountToPay.toLocaleString()} F</strong>
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()}
            </Modal>
        </div>
    );
};

export default RevenuesBonusRechargePage;
